import React, { useEffect, useState } from "react";
import { fetchQuestionById, updateQuestion } from "../pages/services/questionService";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import AlternateAnswersField from "./formComponents/AlternateAnswersField";
import "../styles/EditQuestionModal.css";

// Lightweight editor reused from the Student Response section so admins can
// fix a wrong question/explanation without leaving that page. Its "Answer
// Text" field saves directly into question.answer, which is plain text only
// for Text/Descriptive questions — for MCQ, answer is the correct option's
// id, so that field is disabled here (edit the correct option at
// /questions/:id/update instead) to avoid overwriting it with plain text and
// silently breaking the question everywhere else it's used (e.g. a "quiz"
// section answered by clicking an option).
const EditQuestionModal = ({ isOpen, onClose, response, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [answerExplanation, setAnswerExplanation] = useState("");
  const [alternateAnswers, setAlternateAnswers] = useState([]);
  const [isMCQ, setIsMCQ] = useState(false);

  useEffect(() => {
    if (!isOpen || !response) return;
    setLoading(true);
    setError("");
    fetchQuestionById(response.questionId)
      .then((q) => {
        setQuestion(q.question || "");
        setAnswer(q.answer || "");
        setAnswerExplanation(q.answerExplanation || "");
        setAlternateAnswers(q.alternateAnswers || []);
        setIsMCQ(/multiple choice/i.test(q.questionType || ""));
      })
      .catch(() => setError("Could not load question."))
      .finally(() => setLoading(false));
  }, [isOpen, response]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const cleanAlternateAnswers = alternateAnswers.map((a) => a.trim()).filter(Boolean);
      // Omit `answer` entirely for MCQ — it's never editable here, so never
      // sent, rather than risk overwriting the option id with stale state.
      const payload = { question, answerExplanation, alternateAnswers: cleanAlternateAnswers };
      if (!isMCQ) payload.answer = answer;
      await updateQuestion(response.questionId, payload);
      onSaved({ questionId: response.questionId, question, answer, answerExplanation });
      onClose();
    } catch {
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eqm-overlay">
      <div className="eqm-content">
        <h3 className="eqm-title">Edit Question</h3>

        {loading ? (
          <div className="eqm-loading">Loading…</div>
        ) : (
          <div className="eqm-form">
            {error && <div className="eqm-error">{error}</div>}

            <div className="eqm-field">
              <label>Question Text</label>
              <RichTextEditor
                value={question}
                onChange={setQuestion}
                placeholder="The question text…"
                toolbar="full"
                height={150}
              />
            </div>

            <div className="eqm-field">
              <label>Answer Text</label>
              {isMCQ ? (
                <div className="eqm-mcq-notice">
                  This is a multiple-choice question — its correct answer is one of its
                  options, not plain text, so it can't be edited here.{" "}
                  <a href={`/questions/${response.questionId}/update`} target="_blank" rel="noopener noreferrer">
                    Open the full question editor
                  </a>{" "}
                  to change the correct option or edit the options themselves.
                </div>
              ) : (
                <RichTextEditor
                  value={answer}
                  onChange={setAnswer}
                  placeholder="The correct answer…"
                  toolbar="full"
                  height={130}
                />
              )}
            </div>

            <div className="eqm-field">
              <label>{isMCQ ? "Accepted Alternate Phrasings (optional)" : "Alternate Answers (optional)"}</label>
              <AlternateAnswersField value={alternateAnswers} onChange={setAlternateAnswers} />
            </div>

            <div className="eqm-field">
              <label>Solution Text</label>
              <RichTextEditor
                value={answerExplanation}
                onChange={setAnswerExplanation}
                placeholder="Explanation / solution…"
                toolbar="full"
                height={150}
              />
            </div>

            <div className="eqm-actions">
              <button className="eqm-cancel-btn" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="eqm-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditQuestionModal;
